/**
 * Maintenance utilities for Verstaani.
 *
 * Helpers for sorting and managing maintenance data.
 */

/**
 * Sorts maintenances by priority: in_progress first, optionally completed last, then by schedule.
 *
 * Priority order:
 * 1. in_progress items come first
 * 2. completed items come last (if completedLast is true)
 * 3. Items with schedule sorted by start_time (soonest first)
 * 4. Items without schedule sorted by created_at (newest first)
 *
 * @param {Array} maintenances - Array of maintenance objects
 * @param {object} options - Sorting options
 * @param {boolean} options.completedLast - Put completed items last (default: false)
 * @returns {Array} - Sorted maintenances (new array, does not mutate input)
 */
export const sortMaintenancesByPriority = (maintenances, options = {}) => {
  const { completedLast = false } = options;

  return [...maintenances].sort((a, b) => {
    // in_progress always comes first
    if (a.state === 'in_progress' && b.state !== 'in_progress') return -1;
    if (a.state !== 'in_progress' && b.state === 'in_progress') return 1;

    // completed comes last (if enabled)
    if (completedLast) {
      if (a.state === 'completed' && b.state !== 'completed') return 1;
      if (a.state !== 'completed' && b.state === 'completed') return -1;
    }

    // Sort by schedule start_time (soonest first)
    const aTime = a.schedules?.[0]?.start_time ? new Date(a.schedules[0].start_time) : null;
    const bTime = b.schedules?.[0]?.start_time ? new Date(b.schedules[0].start_time) : null;

    if (aTime && bTime) return aTime - bTime;
    if (aTime && !bTime) return -1;
    if (!aTime && bTime) return 1;

    // Fall back to created_at (newest first)
    return new Date(b.created_at) - new Date(a.created_at);
  });
};
